// DP245 — the one task popup: create and edit a to-do. Replaces the old dialog.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { KmConfirm } from '@/components/KmConfirm';
import { QUADRANTS, quadrantOf, priorityFor } from '../../utils/quadrant';
import { formatDay } from '../../utils/workingDays';
import '@/styles/project-desk-mockup.css';
import '@/styles/project-desk.css';
import '@/styles/tasks-page.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Q_ORDER = ['now', 'sched', 'del', 'later'];
const REPEATS = [['none', 'Never'], ['daily', 'Daily'], ['weekly', 'Weekly'], ['monthly', 'Monthly']];

const localDay = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const draftFrom = (task) => ({
  title: task?.title || '',
  project_id: task?.project_id || null,
  priority: task?.priority || 'medium',
  due_date: task ? String(task.due_date || '').slice(0, 10) : localDay(),
  due_time: task?.due_time || '',
  assignee_id: task?.assignee_id || null,
  assignee_type: task?.assignee_type || null,
  assigned_to: task?.assigned_to || null,
  category: task?.category || '',
  is_recurring: !!task?.is_recurring,
  recurrence: (task?.is_recurring && (task?.recurrence?.pattern || task?.recurrence?.frequency)) || 'none',
  subtasks: Array.isArray(task?.subtasks) ? task.subtasks.map(s => ({ ...s })) : [],
  description: task?.description || '',
});

export default function TaskDialog({ open, task, projects = [], me, onClose, onSaved, noteId }) {
  const [d, setD] = useState(draftFrom(task));
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [cats, setCats] = useState([]);
  const [delIntent, setDelIntent] = useState(false);
  const [step, setStep] = useState('');
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);
  const saving = useRef(false);
  const stepRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setD(draftFrom(task));
    setDelIntent(false);
    setStep('');
  }, [open, task]);

  useEffect(() => {
    if (!open) return;
    let off = false;
    (async () => {
      const [u, c, s] = await Promise.all([
        axios.get(`${API}/users`).catch(() => ({ data: { users: [] } })),
        axios.get(`${API}/contacts/list`).catch(() => ({ data: [] })),
        axios.get(`${API}/spec-categories`).catch(() => ({ data: { categories: [] } })),
      ]);
      if (off) return;
      setUsers((u.data?.users || []).filter(x => x && x.id));
      const raw = Array.isArray(c.data) ? c.data : (c.data?.contacts || []);
      setContacts(raw.map(x => ({ id: x.id, name: `${x.first_name || ''} ${x.last_name || ''}`.trim() || x.company || x.email || 'Contact', company: x.company })));
      setCats(s.data?.categories || []);
    })();
    return () => { off = true; };
  }, [open]);

  const set = (k, v) => setD(p => ({ ...p, [k]: v }));
  const userName = (id) => { const u = users.find(x => x.id === id); return u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email : null; };
  const mine = !d.assignee_id || d.assignee_id === me;
  const personLabel = mine ? 'Me' : (d.assignee_type === 'contact' ? (contacts.find(c => c.id === d.assignee_id) || {}).name : userName(d.assignee_id)) || d.assigned_to || 'Someone';
  const quadrant = useMemo(() => quadrantOf(d, me), [d, me]);
  const shown = quadrant || (delIntent ? 'del' : null);
  const needsPerson = shown === 'del' && mine;

  const pill = (q) => {
    if (q === shown) {                                   // click again clears
      if (q === 'del' && !mine) {
        toast.info(`given to ${personLabel} — set Person to Me to clear`);
        return;
      }
      setDelIntent(false);
      set('priority', 'medium');
      return;
    }
    setDelIntent(q === 'del');
    set('priority', priorityFor(q));
  };

  const pickPerson = (v) => {
    if (!v) { setD(p => ({ ...p, assignee_id: null, assignee_type: null, assigned_to: null })); return; }
    setD(p => ({ ...p, assignee_id: v.id, assignee_type: v.type, assigned_to: v.name }));
    setDelIntent(false);
  };

  const commitPendingStep = () => {
    const t = step.trim();
    if (!t) return d.subtasks;
    const next = [...d.subtasks, { id: `s-${Date.now()}`, title: t, completed: false }];
    setD(p => ({ ...p, subtasks: next }));
    setStep('');
    return next;
  };

  const addStep = () => { commitPendingStep(); stepRef.current?.focus(); };

  const save = async () => {
    if (!d.title.trim()) { toast.error('A task needs a name'); return; }
    if (needsPerson) { toast.error('Who has it? Pick a person, or choose another quadrant'); return; }
    if (saving.current) return;
    saving.current = true;
    setBusy(true);
    const subtasks = commitPendingStep();
    const noPerson = !d.assignee_id;
    const body = {
      title: d.title.trim(),
      description: d.description,
      project_id: d.project_id,
      priority: d.priority,
      due_date: d.due_date || null,
      due_time: d.due_time || null,
      category: d.category || null,
      is_recurring: d.recurrence !== 'none',
      recurrence: d.recurrence === 'none' ? null : { pattern: d.recurrence, interval: 1 },
      subtasks,
      // DP249 amendment — Me means nobody else holds it, so the person is emptied.
      ...(noPerson
        ? { clear: ['assignee_id', 'assignee_type', 'assigned_to'] }
        : { assignee_id: d.assignee_id, assignee_type: d.assignee_type, assigned_to: d.assigned_to }),
    };
    try {
      if (task?.id) {
        await axios.put(`${API}/tasks/${task.id}`, { ...body, inbox: !!task.inbox && quadrantOf(d, me) === null });
      } else {
        // DP258 — a task made from a memo keeps the memo's id, so the two
        // can be told apart from a task typed by hand.
        await axios.post(`${API}/tasks`, { ...body, kind: 'todo', status: 'todo', created_by: me, inbox: quadrantOf(d, me) === null, ...(noteId ? { note_id: noteId } : {}) });
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save that');
    } finally {
      saving.current = false;
      setBusy(false);
    }
  };

  const archive = async () => {
    try { await axios.post(`${API}/tasks/${task.id}/archive`); onSaved?.(); onClose?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not archive that'); }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/tasks/${task.id}`); setConfirmDel(false); onSaved?.(); onClose?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete that'); }
  };

  const live = projects.filter(p => p.status !== 'archived').slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  const catOptions = cats.map(c => [c.prefix, `${c.prefix} · ${c.name}`]);
  const legacyCat = d.category && !cats.some(c => c.prefix === d.category);
  const history = [
    task?.created_at && `captured ${formatDay(String(task.created_at).slice(0, 10))}${task.source ? ` · ${task.source}` : ''}`,
    task?.updated_at && `last touched ${formatDay(String(task.updated_at).slice(0, 10))}`,
    task?.completed_at && `done ${formatDay(String(task.completed_at).slice(0, 10))}`,
    task?.archived_at && `archived ${formatDay(String(task.archived_at).slice(0, 10))}`,
  ].filter(Boolean);

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
        <DialogContent className="kpd-modal kmt-dlg" data-testid="task-dialog">
          <DialogHeader>
            <DialogTitle className="kpd-modal__ttl" data-testid="task-dialog-title">{task?.id ? 'Task' : 'New task'}</DialogTitle>
          </DialogHeader>

          <div className="kpd-modal__stack">
            <label className="kpd-modal__lbl" htmlFor="kmt-what">What</label>
            <input id="kmt-what" className="kmt-dlg__in" value={d.title} autoFocus={!task?.id}
              onChange={e => set('title', e.target.value)} placeholder="What needs doing?" data-testid="task-dialog-title-input" />

            <div className="kmt-dlg__two">
              <div>
                <label className="kpd-modal__lbl" htmlFor="kmt-home">Home</label>
                <select id="kmt-home" className="kmt-dlg__in" data-testid="task-dialog-home"
                  value={d.project_id || ''} onChange={e => set('project_id', e.target.value || null)}>
                  <option value="">Loose</option>
                  {live.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <p className="kmt-dlg__hint">{d.project_id ? (live.find(p => p.id === d.project_id) || {}).name || 'the team can see it' : 'loose — only you, until you share it'}</p>
              </div>

              <div>
                <span className="kpd-modal__lbl">Quadrant</span>
                <div className="kmt-dlg__pills" data-testid="task-dialog-quadrant">
                  {Q_ORDER.map(q => (
                    <button key={q} type="button" className="kmt-pill" data-q={q} aria-pressed={shown === q}
                      onClick={() => pill(q)} data-testid={`task-dialog-q-${q}`}>{QUADRANTS[q].label}</button>
                  ))}
                </div>
                <p className="kmt-dlg__hint" data-testid="task-dialog-quadrant-hint">
                  {needsPerson ? 'who has it? pick a person'
                    : shown === 'del' ? `given to ${personLabel} — set Person to Me to clear`
                    : shown ? 'click it again to clear' : 'no quadrant is fine — it just sits in the list'}
                </p>
              </div>
            </div>

            <div className="kmt-dlg__three">
              <div>
                <label className="kpd-modal__lbl" htmlFor="kmt-date">Date</label>
                <input id="kmt-date" type="date" className="kmt-dlg__in" value={d.due_date} onChange={e => set('due_date', e.target.value)} data-testid="task-dialog-date" />
              </div>
              <div>
                <label className="kpd-modal__lbl" htmlFor="kmt-time">Time</label>
                <input id="kmt-time" type="time" className="kmt-dlg__in" value={d.due_time || ''} onChange={e => set('due_time', e.target.value)} data-testid="task-dialog-time" />
              </div>
              <div>
                <label className="kpd-modal__lbl" htmlFor="kmt-person">Person</label>
                <select id="kmt-person" className="kmt-dlg__in" data-testid="task-dialog-person"
                  value={mine ? '' : `${d.assignee_type || 'user'}:${d.assignee_id}`}
                  onChange={e => {
                    if (!e.target.value) { pickPerson(null); return; }
                    const [type, id] = e.target.value.split(':');
                    const name = type === 'contact' ? (contacts.find(c => c.id === id) || {}).name : userName(id);
                    pickPerson({ id, type, name });
                  }}>
                  <option value="">Me</option>
                  {users.filter(u => u.id !== me).map(u => <option key={u.id} value={`user:${u.id}`}>{userName(u.id)}</option>)}
                  {contacts.map(c => <option key={c.id} value={`contact:${c.id}`}>{c.name}{c.company ? ` · ${c.company}` : ''}</option>)}
                </select>
              </div>
            </div>

            <div className="kmt-dlg__two">
              <div>
                <label className="kpd-modal__lbl" htmlFor="kmt-cat">Category</label>
                <select id="kmt-cat" className="kmt-dlg__in" value={d.category || ''} onChange={e => set('category', e.target.value)} data-testid="task-dialog-category">
                  <option value="">None</option>
                  {legacyCat && <option value={d.category}>{d.category}</option>}
                  {catOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="kpd-modal__lbl" htmlFor="kmt-rep">Repeats</label>
                <select id="kmt-rep" className="kmt-dlg__in" value={d.recurrence} onChange={e => set('recurrence', e.target.value)} data-testid="task-dialog-repeats">
                  {REPEATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>

            <div>
              <span className="kpd-modal__lbl">Steps</span>
              <div className="kmt-dlg__steps" data-testid="task-dialog-steps">
                {d.subtasks.map((s, i) => (
                  <div key={s.id || i} className="kmt-dlg__step">
                    <button type="button" className={`kmt-cb${s.completed ? ' is-on' : ''}`} aria-label="Done"
                      onClick={() => setD(p => ({ ...p, subtasks: p.subtasks.map((x, j) => j === i ? { ...x, completed: !x.completed } : x) }))}
                      data-testid={`task-dialog-step-toggle-${i}`} />
                    <span className={s.completed ? 'is-done' : ''}>{s.title}</span>
                    <button type="button" className="kmt-dlg__x" aria-label="Remove"
                      onClick={() => setD(p => ({ ...p, subtasks: p.subtasks.filter((_, j) => j !== i) }))}
                      data-testid={`task-dialog-step-remove-${i}`}>×</button>
                  </div>
                ))}
                <div className="kmt-dlg__steprow kpd-acts">
                  <input ref={stepRef} className="kmt-dlg__in" value={step} onChange={e => setStep(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addStep(); } }}
                    onBlur={() => commitPendingStep()}
                    placeholder="Add a step" data-testid="task-dialog-step-input" />
                  <button type="button" className="kmt-dlg-step-add" aria-label="Add step"
                    onMouseDown={e => e.preventDefault()} onClick={addStep}
                    data-testid="task-dialog-step-add">+</button>
                </div>
              </div>
            </div>

            <div>
              <label className="kpd-modal__lbl" htmlFor="kmt-notes">Notes</label>
              <textarea id="kmt-notes" className="kmt-dlg__in kmt-dlg__notes" value={d.description}
                onChange={e => set('description', e.target.value)} placeholder="Anything worth remembering" data-testid="task-dialog-notes" />
            </div>

            {task?.id && history.length > 0 && (
              <div data-testid="task-dialog-history">
                <span className="kpd-modal__lbl">History</span>
                {history.map((h, i) => <p key={i} className="kmt-dlg__hist">{h}</p>)}
              </div>
            )}
          </div>

          <div className="kpd-acts kpd-modal__acts">
            {task?.id && <button type="button" className="x" onClick={() => setConfirmDel(true)} data-testid="task-dialog-delete">Delete</button>}
            {task?.id && <button type="button" onClick={archive} data-testid="task-dialog-archive">Archive</button>}
            <button type="button" onClick={() => onClose?.()} data-testid="task-dialog-cancel">Cancel</button>
            <button type="button" className="k" onClick={save} disabled={busy} data-testid="task-dialog-save">Save</button>
          </div>
        </DialogContent>
      </Dialog>

      <KmConfirm
        open={confirmDel}
        onOpenChange={setConfirmDel}
        title="Delete this task?"
        message="It goes for good. Archive keeps it out of the way instead."
        actionLabel="Delete task"
        destructive
        onConfirm={remove}
      />
    </>
  );
}
